import numeral from 'numeral';
import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';

import { fetchCollections, fetchCuratedCollections } from '../actions/collections.actions';
import { fetchActiveLicenceModels } from '../actions/licenceModels.actions';
import { fetchPinnedContents } from '../actions/pinned.actions';
import { fetchPublishers } from '../actions/publishers.actions';
import { fetchSchoolTypes } from '../actions/schoolTypes.actions';
import { fetchTeachingFormats, fetchWorkloads } from '../actions/search.actions';
import {
	fetchMe,
	fetchSubscriptionUpgradePreview,
	getExperiment,
	getPendingSchoolLicence,
	getStripeSubscriptionDetails,
	getSubscription,
} from '../actions/user.actions';
import cookies from '../clients/cookies';
import IntercomButton from '../components/IntercomButton';
import type { StoreType } from '../reducers';
import services from '../services';
import { trackingEvents } from '../services/tracking/trackConfig';
import 'numeral/locales/de';

import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import type { RouterState } from 'found';

import { ampli } from '../ampli';
import GoogleTagManager from '../components/GoogleTagManager/GoogleTagManager';
import SearchFeedbackDialog from '../components/modals/FeedbackDialogs/SearchFeedbackDialog';
import SubdomainChecker from '../components/SubdomainChecker';
import { newPricingPackageLicenceIds, packageLicenceIds, routes, userType } from '../constants';
import {
	getSubscription as getSubscriptionSelector,
	selectIsAuthenticated,
	selectUser,
} from '../reducers/user.selectors';
import RedirectIfAiOnlyPlan from '../router/redirect/RedirectIfAiOnlyPlan';
import { mapAmpliUserProperties } from '../utils/ampli';
import { notifyBugsnagHandledError } from '../utils/bugsnagClient';
import { isFeatureEnabled } from '../utils/feature';
import { isSchoolSubdomain } from '../utils/subdomain';
import useAction from '../utils/useAction';
import useConcludeHintOnTargetRoute from '../utils/useConcludeHintOnTargetRoute';
import withRouter from '../utils/withRouter';
import Contexts from './contexts';
import {
	hasUpgradeSuccessMarker,
	stripUpgradeMarker,
	useUpgradeSuccessPoll,
} from './useUpgradeSuccessPoll';

numeral.locale('de');

const AppPage: React.FunctionComponent<RouterState & { children?: React.ReactNode }> = ({
	children,
	match: { location },
}) => {
	const { pathname } = location;
	const user = useSelector(selectUser);
	const subscription = useSelector(getSubscriptionSelector);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const fetchMeAction = useAction(fetchMe);
	const isAmpliEnabled = isFeatureEnabled('ampli');

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isAuthenticated) {
			fetchMeAction();
		}
	}, [cookies.getAuthToken()]);

	useConcludeHintOnTargetRoute(location);

	// SM-202: after a successful resubscribe payment Stripe redirects to
	// `/?upgrade=success`; soft-poll `fetchMe` until the provisioning webhook
	// saga flips the user off basis (or we time out), then strip the marker.
	// Redux + React handle the UI refresh — no hard reload. Full rationale +
	// retry shape lives in the hook. All three gates below must be true, else
	// the hook is inert on this render — no URL read, no poll, no side effect:
	//  - `hasUpgradeMarker` — `?upgrade=success` is on the URL; computed once
	//     on mount because Stripe redirects via a hard browser navigation, so
	//     the marker is always present at boot or never.
	//  - `isAuthenticated` — we can't fetch /me without a token.
	//  - `isStripeUser` — the marker is only ever appended by kimuno's
	//     resubscribe checkout (Stripe-only path); a non-Stripe user hitting
	//     the marker means the URL was pasted / bookmarked with nothing to
	//     poll for.
	//
	// `isDone` reads `store.getState().user.me?.type` directly rather than
	// through a React ref: `FETCH_ME.SUCCESS` mutates store state
	// synchronously with the dispatch, so the check catches the tier change
	// on the same iteration that fetched it (a ref-updating effect wouldn't
	// fire until React commits the next render — one microtask too late).
	const hasUpgradeMarker = useMemo(() => hasUpgradeSuccessMarker(), []);
	const store = useStore<StoreType>();
	useUpgradeSuccessPoll({
		enabled: hasUpgradeMarker && isAuthenticated && !!user?.isStripeUser,
		// `fetchMe()` short-circuits when the user is already in Redux
		// (`user.actions.ts:86-88`), so every poll iteration MUST pass `true`
		// to force a real /me request. Without this the loop drains without
		// ever hitting the network and `user.type` never updates.
		refetch: () => fetchMeAction(true),
		// "Done" means the provisioning webhook has landed and the user is
		// no longer basis. Reads directly from the Redux store (not from a
		// React ref) so the check sees state synchronously with the
		// `FETCH_ME.SUCCESS` dispatch — a ref-updating effect only fires after
		// React commits the next render, which is too late for the same-loop
		// iteration that fetched the update.
		//
		// Uses the raw `me.type !== basis` comparison rather than the derived
		// `isBasisUser` selector because `store.getState()` gives us raw state
		// (selectors are applied by `useSelector`, not by getState).
		isDone: () => {
			const currentType = store.getState().user.me?.type;
			return currentType !== undefined && currentType !== userType.basis;
		},
	});

	// Fallback marker cleanup. The poll hook only strips `?upgrade=success`
	// when it actually runs — enabled + polling to completion. But the same
	// marker can end up on the URL for a user the poll intentionally skips
	// (e.g. Zuora / walk-in / school user who followed a pasted or bookmarked
	// link). Without this fallback the marker would linger in the address bar
	// forever, and any later reload would re-enter the same skipped branch.
	//
	// Fires only after auth + user state have hydrated (both non-null) so we
	// don't strip the marker before the poll has had a chance to claim it.
	useEffect(() => {
		if (!hasUpgradeMarker) return;
		if (!isAuthenticated) return;
		if (!user) return;
		if (user.isStripeUser) return;
		stripUpgradeMarker();
	}, [hasUpgradeMarker, isAuthenticated, user]);

	const userId = user && user.id;
	const trackedPageViewKeyRef = useRef<string | null>(null);

	const fetchCollectionsAction = useAction(fetchCollections);
	const fetchCuratedCollectionsAction = useAction(fetchCuratedCollections);
	const fetchPinnedContentsAction = useAction(fetchPinnedContents);
	const getSubscriptionAction = useAction(getSubscription);
	const fetchPublishersAction = useAction(fetchPublishers);
	const fetchSchoolTypesAction = useAction(fetchSchoolTypes);
	const fetchWorkloadsAction = useAction(fetchWorkloads);
	const fetchTeachingFormatsAction = useAction(fetchTeachingFormats);
	const getExperimentAction = useAction(getExperiment);
	const fetchActiveLicenceModelsAction = useAction(fetchActiveLicenceModels);
	const fetchSubscriptionUpgradePreviewData = useAction(fetchSubscriptionUpgradePreview);
	const fetchStripeSubscriptionDetails = useAction(getStripeSubscriptionDetails);
	const fetchPendingSchoolLicence = useAction(getPendingSchoolLicence);

	const fetchStateDataForLoggedUser = () => {
		fetchCollectionsAction();
		fetchCuratedCollectionsAction();
		fetchPinnedContentsAction({ sortOrder: 'desc', version: 2 });
		getSubscriptionAction();
		fetchPublishersAction();
		fetchSchoolTypesAction();
		fetchWorkloadsAction();
		fetchTeachingFormatsAction();
		getExperimentAction('GRO-6');
		getExperimentAction('WIDERRUF');
		// SM-92 Phase 6: hydrate the Stripe-side subscription status (incl.
		// `status: 'trialing'`) so global gates — sidebar upgrade button, page
		// redirects — can react to it. Quietly swallow errors; the legacy
		// Zuora-only state stays fine without it.
		if (user?.isStripeUser) {
			fetchStripeSubscriptionDetails().catch(() => {
				/* no-op */
			});
		}
		// Only a free teacher whose session links them to a school can be waiting
		// for a school licence; admins have the dashboard banner instead. The
		// thunk owns its errors.
		if (user?.isBasisUser && user.groupId && !user.isSchoolAdmin) {
			fetchPendingSchoolLicence();
		}
	};

	const fetchGeneralStateData = () => {
		fetchActiveLicenceModelsAction();
	};

	// this is to fix the issue with the viewport height on mobile devices
	// https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
	function setViewportHeight() {
		const vh = window.innerHeight * 0.01;
		document.documentElement.style.setProperty('--vh', `${vh}px`);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		setViewportHeight();
		window.addEventListener('resize', setViewportHeight);
		return () => {
			window.removeEventListener('resize', setViewportHeight);
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user) {
			fetchStateDataForLoggedUser();
			services.track.eventTrack(trackingEvents.DLpush, { user });
		}
		fetchGeneralStateData();
	}, [userId]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isSchoolSubdomain(window.location.hostname)) {
			cookies.deleteNonEssential();
		}
	}, [cookies.getAllCookies()]);

	// Trial information is coming delayed from zuora
	// therefore we have a separate effect to trigger a data layer push again if isInTrial: true
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user?.isInTrial) {
			services.track.eventTrack(trackingEvents.DLpush, { user });
		}
	}, [user?.isInTrial]);

	useEffect(() => {
		if (pathname.split('/')[1] !== routes.search.root) {
			window.scrollTo(0, 0);
		}
	}, [pathname]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isAuthenticated && !user) {
			// If the user is authenticated, but no user exists in the state
			// then the user is still in the process of being fetched, so we
			// should wait until the user has been created
			return;
		}
		const pageViewKey = `${userId}:${pathname}`;
		if (trackedPageViewKeyRef.current === pageViewKey) return;
		trackedPageViewKeyRef.current = pageViewKey;
		services.track.pageTrack(pathname, { user });
	}, [userId, pathname]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		// Determine which licence model to use based on current subscription
		let licenceModelId: number | undefined;

		if (subscription?.licence?.licenceModelId) {
			const currentLicenceId = Number(subscription.licence.licenceModelId);

			// Check if current subscription uses new pricing
			const currentSubscriptionUsesNewPricing = Object.values(newPricingPackageLicenceIds).includes(
				currentLicenceId
			);
			if (currentSubscriptionUsesNewPricing) {
				licenceModelId = newPricingPackageLicenceIds.meinUnterricht360KI;
			} else {
				licenceModelId = user?.isBibliothekUser
					? packageLicenceIds.meinUnterricht360ForDocumentUsers
					: packageLicenceIds.meinUnterricht360;
			}
		}

		if (user && user.isBasisUser && !subscription) {
			return;
		}

		if (user && user.id && licenceModelId && subscription) {
			fetchSubscriptionUpgradePreviewData(licenceModelId);
		}
	}, [user?.id, subscription]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isAmpliEnabled) return;
		if (userId) {
			ampli.identify(userId);
			ampli.client.add(engagementPlugin());
		}
	}, [userId]);

	// Handle Amplitude user identification with proper user properties
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isAmpliEnabled) return;
		// Add schoolId for school admins without subscription
		if (userId && user?.isSchoolAdmin && !subscription && user?.groupId) {
			// Prepare user properties
			const userProperties = mapAmpliUserProperties({
				schoolId: user.groupId,
			});

			ampli.identify(userId, userProperties);
		}
	}, [userId, user?.isSchoolAdmin, user?.groupId, subscription]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isAmpliEnabled) return;
		try {
			if (user?.isSchoolAdmin && userId) {
				const currentAmpliUserId = ampli.client.getUserId();

				// if current user don't have userId in Amplitude, we need to identify him
				if (!currentAmpliUserId) {
					ampli.identify(userId);
				}
			}
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	}, [userId, user?.isSchoolAdmin]);

	return (
		<Contexts>
			<RedirectIfAiOnlyPlan>{children}</RedirectIfAiOnlyPlan>
			<SearchFeedbackDialog pathname={pathname} userId={userId || ''} />
			<SubdomainChecker />
			{isFeatureEnabled('intercom') && <IntercomButton />}
			{isFeatureEnabled('googleAnalytics') && <GoogleTagManager />}
		</Contexts>
	);
};

export default withRouter(AppPage);
